import { IconCollection } from "components/Icon/IconCollection";
import InfoPanel from "components/Layout/InfoPanel";
import Placeholder from "components/Layout/Placeholder";
import RecordLabel from "components/RecordLabel";
import { useResourceData } from "containers/Resources/screens/ResourcesSingle/useResourceData";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useMount } from "react-use";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { getRelativeUrl } from "utils/service";
import { sortAlphabetically } from "utils/sorting";
import { getSearchObj } from "utils/utils";

export const AccessList = () => {
    const dispatch = useDispatch();
    const { isFetching, resourceName, recordId } = useResourceData();

    const [isLoading, setIsLoading] = useState(false);
    const [roleListWithAccess, setRoleListWithAccess] = useState({});

    const getRoleList = useCallback(
        (permissionList) =>
            permissionList?.reduce((acc, currentPermission) => {
                return {
                    ...acc,
                    [currentPermission.role_id]: {
                        id: currentPermission.role_id,
                        name: currentPermission.role_id_role.name,
                    },
                };
            }, {}),
        []
    );

    const isProject = useMemo(() => resourceName === "project", [resourceName]);
    const isBundle = useMemo(() => resourceName === "bundle", [resourceName]);
    const isWorkspace = useMemo(() => resourceName === "workspace", [resourceName]);

    const fetchRolePermissions = useCallback(() => {
        if (!isProject && !isBundle && !isWorkspace) return;

        setIsLoading(true);

        return new Promise((resolve, reject) => {
            const allProjectsField = getSearchObj({ field: "project_id", op: "eq", value: null });
            const currentProjectField = getSearchObj({
                field: "project_id",
                op: "eq",
                value: recordId,
            });
            const readOperationField = getSearchObj({
                field: "operation",
                op: "eq",
                value: "READ",
            });
            const bundleTypeField = getSearchObj({
                field: "table_type",
                op: "eq",
                value: "BUNDLE",
            });
            const allOperationField = getSearchObj({
                field: "operation",
                op: "eq",
                value: null,
            });
            const allTypeField = getSearchObj({ field: "table_type", op: "eq", value: null });

            const filter = isWorkspace
                ? [allProjectsField]
                : isProject
                ? [{ or: [allProjectsField, currentProjectField] }]
                : [
                      allProjectsField,
                      {
                          and: [
                              {
                                  or: [
                                      { and: [readOperationField, bundleTypeField] },
                                      {
                                          and: [
                                              allTypeField,
                                              { or: [readOperationField, allOperationField] },
                                          ],
                                      },
                                  ],
                              },
                          ],
                      },
                  ];

            const filterStr = JSON.stringify({
                and: filter,
            });
            dispatch({
                type: dataStoreAction.START_FETCH_DATA,
                promise: { resolve, reject },
                key: "rolePermissionList",
                params: {
                    query: `query ($filter: String, $offset: Int, $order: String, $limit: Int){
                            role_permission_plain_list(filter: $filter, offset: $offset, order: $order, limit: $limit)  {
                                project_id
                                role_id
                                role_id_role {
                                    name
                                }
                            }
                        }`,
                    variables: {
                        offset: 0,
                        order: "+id",
                        limit: 500,
                        filter: filterStr,
                    },
                },
            });
        })
            .then((data: any) => {
                const permissionList = data?.role_permission_plain_list;
                setRoleListWithAccess(getRoleList(permissionList));
            })
            .catch((error) => error)
            .finally(() => setIsLoading(false));
    }, [dispatch, getRoleList, isBundle, isProject, isWorkspace, recordId]);

    useMount(() => fetchRolePermissions());

    const roleListSortedAlphabetically = useMemo(
        () => sortAlphabetically([...Object.values(roleListWithAccess)], (type: any) => type.name),
        [roleListWithAccess]
    );

    return (
        <InfoPanel>
            <InfoPanel.Title>{`Roles with access to the current ${resourceName}`}</InfoPanel.Title>
            {isFetching || isLoading ? (
                <Placeholder.List />
            ) : (
                <>
                    {roleListSortedAlphabetically.map((item: any) => {
                        return (
                            <InfoPanel.SingleValue key={item.id}>
                                <RecordLabel
                                    link={getRelativeUrl("role", item.id)}
                                    name={item.name}
                                    icon={IconCollection.badge}
                                />
                            </InfoPanel.SingleValue>
                        );
                    })}
                </>
            )}
        </InfoPanel>
    );
};
